import Phaser from "phaser";
import { fetchLeaderboard, formatTime, type LeaderboardEntry } from "../net/leaderboard";
import { makeOverlay } from "../ui/leaderboardUi";

interface LeaderboardData {
  savedId?: number; // the just-submitted row to highlight, if any
}

/**
 * The ranked leaderboard. Reachable after submitting a score (with the player's
 * row highlighted) or from Level Select (read-only). Rendered as a DOM overlay
 * (see leaderboardUi.ts). Handles loading and error/retry states.
 */
export class LeaderboardScene extends Phaser.Scene {
  private overlay?: HTMLDivElement;
  private savedId?: number;

  constructor() {
    super("Leaderboard");
  }

  init(data: LeaderboardData) {
    this.savedId = data?.savedId;
  }

  create() {
    const { width, height } = this.scale;
    this.add.rectangle(width / 2, height / 2, width, height, 0x0e0f13);

    this.overlay = makeOverlay();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());

    this.renderLoading();
    void this.load2();
  }

  private async load2() {
    try {
      const entries = await fetchLeaderboard(100);
      this.renderTable(entries);
    } catch {
      this.renderError();
    }
  }

  private card(bodyHtml: string): HTMLFormElement {
    const overlay = this.overlay!;
    overlay.innerHTML = `<div class="lb-card">${bodyHtml}</div>`;
    return overlay.querySelector<HTMLFormElement>(".lb-card")!;
  }

  private renderLoading() {
    this.card(`
      <div class="lb-title">Leaderboard</div>
      <div class="lb-muted">Loading...</div>
    `);
  }

  private renderError() {
    const card = this.card(`
      <div class="lb-title">Leaderboard</div>
      <div class="lb-error">Could not load the leaderboard.</div>
      <div class="lb-actions">
        <button class="lb-btn lb-btn-secondary" type="button" data-back>Back</button>
        <button class="lb-btn" type="button" data-retry>Retry</button>
      </div>
    `);
    card.querySelector<HTMLButtonElement>("[data-retry]")!.addEventListener("click", () => {
      this.renderLoading();
      void this.load2();
    });
    card.querySelector<HTMLButtonElement>("[data-back]")!.addEventListener("click", () => this.back());
  }

  private renderTable(entries: LeaderboardEntry[]) {
    const rows = entries
      .map((e) => {
        const me = this.savedId != null && e.id === this.savedId;
        return `
          <tr class="${me ? "lb-row-me" : ""}">
            <td class="num">${e.rank}</td>
            <td>${escapeHtml(e.name)}${me ? " (you)" : ""}</td>
            <td class="num">${e.points}</td>
            <td class="num">${e.collectibles}</td>
            <td class="num">${e.sheepies}</td>
            <td class="num">${formatTime(e.timeMs)}</td>
          </tr>`;
      })
      .join("");

    const empty = `<div class="lb-muted">No scores yet. Be the first!</div>`;
    const table = `
      <table class="lb-table">
        <thead>
          <tr>
            <th class="num">#</th><th>Name</th>
            <th class="num">Points</th><th class="num">Items</th>
            <th class="num">Sheepies</th><th class="num">Time</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`;

    const card = this.card(`
      <div class="lb-title">Leaderboard</div>
      ${entries.length ? table : empty}
      <div class="lb-actions">
        <button class="lb-btn" type="button" data-back>Back to Level Select</button>
      </div>
    `);
    card.querySelector<HTMLButtonElement>("[data-back]")!.addEventListener("click", () => this.back());
  }

  private back() {
    this.scene.start("LevelSelect");
  }

  private teardown() {
    this.overlay?.remove();
    this.overlay = undefined;
  }
}

/** Escape user-entered names before injecting into the table markup. */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#39;";
    }
  });
}
